import { Injectable } from "@angular/core";
import { Functions, httpsCallable } from "@angular/fire/functions";
import {
  createFunctionResult,
  FUNCTION_RESULT_STATUS,
  FunctionResult,
} from "@nova/common-interfaces";
import { FUNCTION_NAME, IPaymentMethod } from "@rfm/shared-interfaces";
import Stripe from "stripe";

import { BehaviorSubject, Observable } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class ThirdPartyStripeService {
  private _isLoading = new BehaviorSubject<boolean>(false);
  isLoading$: Observable<boolean> = this._isLoading.asObservable();

  constructor(protected functions: Functions) {}

  getPublicKey(): string {
    return "pk_test_51O87KxAawgYYcPfzODi6bwtDlXoTsuDQ9DGFXwCXnPYUeqn4cXmqNFscQNh0nAUdtUXxoWSg568jbv182ov4KrQF00Es7p93Nm";
  }

  removePaymentMethod(id: string) {
    this._isLoading.next(true);

    return new Promise<FunctionResult<Stripe.PaymentIntent>>(
      (resolve, reject) => {
        const callable = httpsCallable<
          any,
          FunctionResult<Stripe.PaymentIntent>
        >(
          this.functions,
          FUNCTION_NAME.THIRDPARTY_STRIPE_REMOVE_PAYMENT_METHOD
        );
        callable(id)
          .then(
            (result) => {
              resolve(
                createFunctionResult<Stripe.PaymentIntent>(
                  FUNCTION_RESULT_STATUS.SUCCESS,
                  result.data.data
                )
              );
            },
            (error) => {
              console.error(error);
              reject(
                createFunctionResult<Stripe.PaymentIntent>(
                  FUNCTION_RESULT_STATUS.ERROR,
                  undefined
                )
              );
            }
          )
          .finally(() => {
            this._isLoading.next(false);
          });
      }
    );
  }

  createCustomer(id: string, name: string) {
    this._isLoading.next(true);

    return new Promise<FunctionResult<Stripe.Customer>>((resolve, reject) => {
      const callable = httpsCallable<any, FunctionResult<Stripe.Customer>>(
        this.functions,
        FUNCTION_NAME.THIRDPARTY_STRIPE_CREATE_CUSTOMER
      );
      callable({ id: id, name: name })
        .then(
          (result) => {
            resolve(
              createFunctionResult<Stripe.Customer>(
                FUNCTION_RESULT_STATUS.SUCCESS,
                result.data.data
              )
            );
          },
          (error) => {
            console.error(error);
            reject(
              createFunctionResult<Stripe.PaymentIntent>(
                FUNCTION_RESULT_STATUS.ERROR,
                undefined
              )
            );
          }
        )
        .finally(() => {
          this._isLoading.next(false);
        });
    });
  }

  setDefaultPaymentMethod(customer_id: string, payment_method_id: string) {
    this._isLoading.next(true);

    return new Promise<FunctionResult<boolean>>((resolve, reject) => {
      const callable = httpsCallable<any, FunctionResult<boolean>>(
        this.functions,
        FUNCTION_NAME.THIRDPARTY_STRIPE_SET_DEFAULT_PAYMENT_METHOD
      );
      callable({
        customer_id: customer_id,
        payment_method_id: payment_method_id,
      })
        .then(
          (result) => {
            resolve(
              createFunctionResult<boolean>(
                FUNCTION_RESULT_STATUS.SUCCESS,
                result.data.data
              )
            );
          },
          (error) => {
            console.error(error);
            reject(
              createFunctionResult<Stripe.PaymentIntent>(
                FUNCTION_RESULT_STATUS.ERROR,
                undefined
              )
            );
          }
        )
        .finally(() => {
          this._isLoading.next(false);
        });
    });
  }

  createPaymentIntent(
    customer: string,
    amount: number,
    currency: string,
    autopay: boolean,
    payment_method_id?: string | undefined
  ): Promise<FunctionResult<Stripe.PaymentIntent>> {
    this._isLoading.next(true);

    return new Promise<FunctionResult<Stripe.PaymentIntent>>(
      (resolve, reject) => {
        const callable = httpsCallable<
          any,
          FunctionResult<Stripe.PaymentIntent>
        >(
          this.functions,
          FUNCTION_NAME.THIRDPARTY_STRIPE_CREATE_PAYMENT_INTENT
        );
        callable({
          payload: {
            amount: amount,
            currency: currency,
          },
          customer: customer,
          autopay: autopay,
          payment_method: payment_method_id
        })
          .then(
            (result) => {
              resolve(
                createFunctionResult<Stripe.PaymentIntent>(
                  FUNCTION_RESULT_STATUS.SUCCESS,
                  result.data.data
                )
              );
            },
            (error) => {
              console.error(error);
              reject(
                createFunctionResult<Stripe.PaymentIntent>(
                  FUNCTION_RESULT_STATUS.ERROR,
                  undefined
                )
              );
            }
          )
          .finally(() => {
            this._isLoading.next(false);
          });
      }
    );
  }

  setupPaymentIntent(
    customer: string
  ): Promise<FunctionResult<Stripe.SetupIntent>> {
    this._isLoading.next(true);

    return new Promise<FunctionResult<Stripe.SetupIntent>>(
      (resolve, reject) => {
        const callable = httpsCallable<any, FunctionResult<Stripe.SetupIntent>>(
          this.functions,
          FUNCTION_NAME.THIRDPARTY_STRIPE_SETUP_PAYMENT_INTENT
        );
        callable({
          customer: customer,
        })
          .then(
            (result) => {
              resolve(
                createFunctionResult<Stripe.SetupIntent>(
                  FUNCTION_RESULT_STATUS.SUCCESS,
                  result.data.data
                )
              );
            },
            (error) => {
              console.error(error);
              reject(
                createFunctionResult<Stripe.SetupIntent>(
                  FUNCTION_RESULT_STATUS.ERROR,
                  undefined
                )
              );
            }
          )
          .finally(() => {
            this._isLoading.next(false);
          });
      }
    );
  }

  getPaymentMethods(
    customer: string
  ): Promise<FunctionResult<IPaymentMethod[]>> {
    this._isLoading.next(true);

    return new Promise<FunctionResult<IPaymentMethod[]>>((resolve, reject) => {
      const callable = httpsCallable<string, FunctionResult<IPaymentMethod[]>>(
        this.functions,
        FUNCTION_NAME.THIRDPARTY_STRIPE_GET_PAYMENT_METHODS
      );
      callable(customer)
        .then(
          (result) => {
            resolve(
              createFunctionResult<IPaymentMethod[]>(
                FUNCTION_RESULT_STATUS.SUCCESS,
                result.data.data
              )
            );
          },
          (error) => {
            console.error(error);
            reject(
              createFunctionResult<string | null>(
                FUNCTION_RESULT_STATUS.ERROR,
                null
              )
            );
          }
        )
        .finally(() => {
          this._isLoading.next(false);
        });
    });
  }
}
